import { ColumnDef } from '@tanstack/react-table';
import { useEffect } from 'react';
import { LinkCell } from '../../../../components/tables/cellComponents/LinkCell';
import { FilterField, FilterForm } from '../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../components/tables/filterForms/FormSelect';
import SFCTable from '../../../../components/tables/SFCTable';
import { useDataContext } from '../../../../context';
import { useToast } from '../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../hooks';
import { useGetAllZipcodesQuery } from '../../../../services/endpoints/places/zipcode';
import { getCurrentUser } from '../../../../services/helper';
import { SFCLocation, SFCZipcode } from '../../../../types/place.types';
import { isGlobal, isLocal } from '../../../../utils/consts';
import { getFilterValue } from '../../../../utils/listFilterSearchParams';
import {Forbidden} from "../../../../components/error/Forbidden";

interface DataRow extends SFCZipcode {}

export const ZipCodesView = () => {
  const { addToast } = useToast();
  const { locationOptions } = useDataContext();
  const currentUser = getCurrentUser();
  const isGlobalUser = isGlobal(currentUser?.user_type_id!);

  const [filters, setFilters] = useListFiltersInUrl(
    { scope: 'all', data: { page: 1, entries: 20 } },
    { omitScopeFromUrl: true }
  );
  const { data, error, isLoading, isFetching } = useGetAllZipcodesQuery(filters, {
    skip: !isGlobalUser
  });

  const filterFormFields: FilterField[] = [
    {
      name: 'code',
      label: 'Code',
      value: getFilterValue(filters.data?.filterBy, 'code')
    },
    {
      name: 'city',
      label: 'City',
      value: getFilterValue(filters.data?.filterBy, 'city')
    },
    {
      name: 'state',
      label: 'State',
      value: getFilterValue(filters.data?.filterBy, 'state')
    },
    {
      name: 'area',
      label: 'Area',
      value: getFilterValue(filters.data?.filterBy, 'area')
    }
  ];

  if (currentUser?.isGlobal) {
    filterFormFields.push({
      name: 'location_id',
      type: 'number',
      label: 'Location',
      element: (
        <FormSelect
          name="location_id"
          label="Location"
          options={locationOptions}
          selected_Value={locationOptions.find(
            (option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'location_id')
          )}
        />
      )
    });
  }

  const columns: ColumnDef<DataRow>[] = [
    {
      header: 'ID',
      accessorKey: 'id'
    },
    {
      header: 'Location',
      accessorKey: 'territory.location',
      enableSorting: false,
      cell: ({ getValue }) => {
        let location = getValue() as SFCLocation;
        return location ? (
          <LinkCell
            content={location?.title ?? 'error'}
            url={`admin/places/location/${location?.id ?? 0}`}
          />
        ) : (
          <>Unassigned</>
        );
      }
    },
    {
      header: 'Code',
      accessorKey: 'code'
    },
    {
      header: 'City',
      accessorKey: 'city'
    },
    {
      header: 'State',
      accessorKey: 'state'
    },
    {
      header: 'Area',
      accessorKey: 'area'
    },
    {
      header: 'Latitude',
      accessorKey: 'latitude'
    },
    {
      header: 'Longitude',
      accessorKey: 'longitude'
    },
    {
      header: 'Tax Returns Filed',
      accessorKey: 'tax_returns_filed'
    },
    {
      header: 'Estimated Population',
      accessorKey: 'estimated_population'
    },
    {
      header: 'Total Wages',
      accessorKey: 'total_wages'
    }
    // {
    //   header: '',
    //   accessorKey: 'blank',
    //   enableSorting: false,
    //   cell: ({ row }) => (
    //     <>
    //       <LinkCell content='View' url={`/admin/places/zip-codes/${row.original.id}`} />
    //       &nbsp;
    //       &nbsp;
    //       <LinkCell content='Edit' url={`/admin/places/zip-codes/edit/${row.original.id}`} />
    //       &nbsp;
    //       &nbsp;
    //       <LinkCell content='Delete' url={`/admin/places/zip-codes/delete/${row.original.id}`} />
    //     </>
    //   )
    // },
  ];

  useEffect(() => {
    if (error) {
      addToast(`Error while loading data: ${JSON.stringify(error)}`, 'error');
    }
  }, [error]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  if (!isGlobalUser) {
    return <Forbidden />;
  }

  if (error || isLoading) {
    return <></>;
  }

  return (
    <div className="py-2">
      <SFCTable
        name={'Zip Codes'}
        columns={columns}
        defaultScope={filters.scope ?? 'all'}
        data={data?.results ?? []}
        count={data?.count}
        isLoading={isLoading}
        isFetching={isFetching}
        indexDownloadPath="zip-codes"
        filters={filters?.data?.filterBy}
        page_Size={filters?.data?.entries}
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        onFiltersChanged={setFilters}
      />
    </div>
  );
};
